import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "itemsContainer",
    "itemTemplate",
    "purchaseOrderSelect",
    "photoPreview",
    "photoInput",
  ];

  connect() {
    // Initialiser les écouteurs d'événements pour les champs file
    this.addFileListeners();
  }

  addFileListeners() {
    this.element.querySelectorAll('input[type="file"]').forEach((input) => {
      input.addEventListener("change", this.previewPhoto.bind(this));
    });
  }

  addItem(event) {
    event.preventDefault();

    const timestamp = new Date().getTime();
    const template = this.itemTemplateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      timestamp
    );

    this.itemsContainerTarget.insertAdjacentHTML("beforeend", template);

    // Ajouter l'écouteur d'événement pour le nouveau champ file
    this.addFileListeners();
  }

  removeItem(event) {
    event.preventDefault();

    const item = event.target.closest(".delivery-item");

    // Si l'élément a un champ _destroy, le marquer pour suppression
    const destroyInput = item.querySelector('input[name*="_destroy"]');
    if (destroyInput) {
      destroyInput.value = 1;
      item.style.display = "none";
    } else {
      // Sinon, simplement supprimer l'élément du DOM
      item.remove();
    }
  }

  // One row per line of the order still to deliver, its quantity prefilled
  // with what remains (QA SE2-08). The select wrapped by SlimSelect fires
  // several change events for one pick: only the last request for the order
  // shown fills the rows, and the rows are replaced, never appended twice.
  loadPurchaseOrderItems() {
    if (!this.hasPurchaseOrderSelectTarget) return;

    const orderId = this.purchaseOrderSelectTarget.value;
    if (!orderId || orderId === this.requestedOrderId) return;

    this.requestedOrderId = orderId;
    const request = (this.orderRequest = Symbol(orderId));

    fetch(`/schools/stocks/purchase_orders/${orderId}.json`)
      .then((response) => response.json())
      .then((data) => {
        if (request !== this.orderRequest) return;

        const remaining = (data.purchase_order_items || []).filter(
          (item) => item.remaining_quantity > 0
        );
        this.fillTemplateOptions(remaining);
        this.itemsContainerTarget.replaceChildren(
          ...remaining.map((item, index) => this.buildOrderRow(item, index))
        );
        this.addFileListeners();
      })
      .catch(() => {
        if (request === this.orderRequest) this.requestedOrderId = null;
      });
  }

  // The lines of the order become the choices of every row, those added by
  // hand included.
  fillTemplateOptions(items) {
    const template = this.itemTemplateTarget;
    const select = template.content.querySelector(
      'select[name*="[purchase_order_item_id]"]'
    );
    if (!select) return;

    const blank = Array.from(select.options).find((opt) => opt.value === "");
    select.replaceChildren(...(blank ? [blank] : []));
    items.forEach((item) => {
      const option = document.createElement("option");
      option.value = item.id;
      option.text = `${item.stock_item.name} (${item.remaining_quantity} ${item.stock_item.unit_of_measure})`;
      select.appendChild(option);
    });
  }

  buildOrderRow(item, index) {
    const html = this.itemTemplateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      `${Date.now()}${index}`
    );
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html.trim();
    const row = wrapper.firstElementChild;

    row.dataset.purchaseOrderItemId = item.id;
    const field = (name) => row.querySelector(`[data-item-field="${name}"]`);
    if (field("purchase_order_item_id")) field("purchase_order_item_id").value = item.id;
    if (field("received_quantity")) field("received_quantity").value = item.remaining_quantity;
    if (field("reception_status")) field("reception_status").value = "accepted";
    const name = row.querySelector(".item-name");
    if (name) name.textContent = item.stock_item.name;
    const expected = row.querySelector(".expected-quantity");
    if (expected) expected.textContent = item.remaining_quantity;

    return row;
  }

  updateReceptionStatus(event) {
    const item = event.target.closest(".delivery-item");
    if (!item) return;
    const receivedQuantity =
      parseInt(
        item.querySelector('input[name*="[received_quantity]"]').value
      ) || 0;
    const expectedQuantity =
      parseInt(item.querySelector(".expected-quantity").textContent) || 0;
    const statusSelect = item.querySelector(
      'select[name*="[reception_status]"]'
    );

    if (receivedQuantity === 0) {
      statusSelect.value = "rejected";
    } else if (receivedQuantity < expectedQuantity) {
      statusSelect.value = "partial";
    } else {
      statusSelect.value = "accepted";
    }
  }

  previewPhoto(event) {
    const input = event.target;
    // The delivery note is a file field outside any item (QA SE-SE-16).
    const item = input.closest(".delivery-item");
    if (!item) return;
    const preview = item.querySelector(".photo-preview");

    if (input.files && input.files[0] && preview) {
      const reader = new FileReader();

      reader.onload = (e) => {
        preview.innerHTML = `<img src="${e.target.result}" class="h-24 w-24 object-cover rounded">`;
      };

      reader.readAsDataURL(input.files[0]);
    }
  }
}
